import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import {
  type AttributionDimension,
  type AttributionFilters,
  getAttributionCohorts,
  getAttributionGroupedTrend,
  getAttributionOverview,
  getMeetingSpeakerLabelMeetings,
  type MeetingSpeakerLabelMeetingFilters,
} from '@/api/attributionObservability'

const errorStatus = (error: unknown) => {
  const status = typeof error === 'object' && error !== null && 'status' in error ? error.status : undefined
  return typeof status === 'number' ? status : undefined
}
export const isAccessDenied = (error: unknown) => [401, 403].includes(errorStatus(error) ?? 0)
export const isMissingEndpoint = (error: unknown) => [404, 405].includes(errorStatus(error) ?? 0)
export const isTransientQueryError = (error: unknown) => {
  const status = errorStatus(error)
  return status !== undefined && (status >= 500 || status === 429)
}

const queryDefaults = {
  staleTime: 30_000,
  gcTime: 0,
  retry: (failureCount: number, error: unknown) =>
    failureCount < 1 && !isAccessDenied(error) && !isMissingEndpoint(error),
} as const

const branchQueryDefaults = {
  ...queryDefaults,
  staleTime: 60_000,
  gcTime: 5 * 60_000,
} as const

export const useAttributionOverview = (filters: AttributionFilters, enabled = true) =>
  useQuery({
    ...queryDefaults,
    enabled,
    refetchInterval: 60_000,
    queryKey: ['attribution-observability', 'overview', filters],
    queryFn: ({ signal }) => getAttributionOverview(filters, signal),
  })

export const useAttributionCohorts = (filters: AttributionFilters, dimension: AttributionDimension) =>
  useQuery({
    ...branchQueryDefaults,
    queryKey: ['attribution-observability', 'cohorts', filters, dimension],
    queryFn: ({ signal }) => getAttributionCohorts(filters, dimension, signal),
  })

export const useMeetingSpeakerLabelMeetings = (
  filters: MeetingSpeakerLabelMeetingFilters,
  cursor?: string,
  enabled = true
) =>
  useQuery({
    ...queryDefaults,
    enabled,
    queryKey: ['attribution-observability', 'meetings', filters, cursor],
    queryFn: ({ signal }) => getMeetingSpeakerLabelMeetings(filters, cursor, signal),
  })

export const useAttributionGroupedTrend = (filters: AttributionFilters, axes: AttributionDimension[], enabled = true) =>
  useQuery({
    ...queryDefaults,
    enabled,
    refetchInterval: 60_000,
    queryKey: ['attribution-observability', 'trend', filters, axes],
    queryFn: ({ signal }) => getAttributionGroupedTrend(filters, axes, signal),
  })

export const useInfiniteMeetingPreview = (filters: MeetingSpeakerLabelMeetingFilters, enabled: boolean) =>
  useInfiniteQuery({
    ...branchQueryDefaults,
    enabled,
    queryKey: ['attribution-observability', 'meeting-preview', filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => getMeetingSpeakerLabelMeetings(filters, pageParam, signal, 10),
    getNextPageParam: (page) => page.next_cursor ?? undefined,
  })
